/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Typography2, breakpoints, typography2Color, useMediaQuery } from '@coursera/cds-core';
import { Link } from '@coursera/event-pulse/react';

import DottedSeparator from 'bundles/unified-description-page-common/components/shared/DottedSeparator';
import Rating from 'bundles/unified-description-page-common/components/shared/Rating';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { COURSE_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import { durationToHours } from 'bundles/unified-description-page-common/utils/learningTime';

import _t from 'i18n!nls/xdp';

export type CourseItemSubtitleElement = React.ReactElement<Props, typeof CourseItemSubtitle>;

interface Props {
  courseIndex: number;
  totalContentDuration?: string | null;
  rating?: number | null;
  ratingCount?: number | null;
  link?: string;
}

const styles = {
  container: css`
    display: flex;
    margin-top: var(--cds-spacing-50);
    white-space: nowrap;

    ${breakpoints.down('sm')} {
      flex-wrap: wrap;
      white-space: unset;
    }
  `,
  viewCourseLink: css`
    ${typography2Color.body};

    &:hover {
      ${typography2Color.body};
    }
  `,
  viewReviewsLink: css`
    ${typography2Color.supportText};

    &:hover {
      ${typography2Color.supportText};
    }
  `,
};

const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.stopPropagation();
};

const CourseItemSubtitle = ({ courseIndex, totalContentDuration, rating, ratingCount, link }: Props) => {
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  const eventingProductData = useEventingProductData();

  return (
    <Typography2 variant="bodySecondary" color="supportText" component="div" css={styles.container}>
      {/* eslint-disable-next-line */}
      <FormattedMessage message={_t('Course {courseIndex}')} courseIndex={courseIndex} />
      {totalContentDuration && (
        <React.Fragment>
          <DottedSeparator />
          <span>{durationToHours(totalContentDuration)}</span>
        </React.Fragment>
      )}
      {rating && link && (
        <React.Fragment>
          <DottedSeparator />
          <Link
            variant="quiet"
            href={`${link}#${COURSE_NAV_ITEMS.REVIEWS}`}
            onClick={handleClick}
            css={styles.viewReviewsLink}
            trackingData={{
              button: { name: 'description_page_view_ratings' },
              pageSection: {
                sectionName: 'syllabus',
              },
              ...eventingProductData,
            }}
          >
            <Rating color="default" rating={rating} ratingCount={ratingCount} />
          </Link>
        </React.Fragment>
      )}
      {!isMobile && (
        <React.Fragment>
          <DottedSeparator />
          <Link
            href={link}
            onClick={handleClick}
            css={styles.viewCourseLink}
            trackingData={{
              button: { name: 'description_page_syllabus_link' },
              pageSection: {
                sectionName: 'syllabus',
              },
              ...eventingProductData,
            }}
          >
            {
              // eslint-disable-next-line no-restricted-syntax
              _t('View course')
            }
          </Link>
        </React.Fragment>
      )}
    </Typography2>
  );
};

export default CourseItemSubtitle;
